import type { AffixSlot, BreakdownPart } from '../exercise'
import type { MiniSet } from './types'

// Мини-задания B1, уроки 56–71 (тикет 06, бриф .scratch/grammar-unify/brief-mini.md).
// Слова и фразы — из уроков правила и пройденного до них.

// Основа слова в «собери слово»: дана, выбирать нечего.
const b = (text: string): AffixSlot => ({ answer: text, options: [], label: '', why: '' })
// Морфема с вариантами выбора (верный — answer, он же среди options).
const m = (answer: string, options: string[], label: string, why: string): AffixSlot => ({ answer, options, label, why })
// Кусочек разбора: основа — без метки, морфема — с меткой и пояснением.
const p = (text: string, label = '', why = ''): BreakdownPart => ({ text, label, why })

export const MINI_B1_2: MiniSet[] = [
  // ── Урок 56: -mekte / -mektedir ─────────────────────────────────────────────
  {
    rule: 'formal-mekte',
    explain:
      'В выпуске новостей, по радио, в объявлениях вместо -yor звучит -mekte, а в третьем лице - -mektedir: Tren istasyona yaklaşmaktadır. По смыслу это то же "сейчас", что -yor, только официально: -mekte после e, i, ö, ü, -makta после a, ı, o, u. В обычном разговоре только -yor.',
    examples: [
      {
        parts: [p('Tren istasyona yaklaş'), p('makta', 'официальное "сейчас"', 'последняя гласная a - -makta'), p('dır', '3-е лицо', 'в третьем лице диктор почти всегда добавляет -dır')],
        ru: 'поезд приближается к станции (объявление)',
      },
      {
        parts: [p('Kar yağmaya devam et'), p('mekte', 'официальное "сейчас"', 'последняя гласная e - -mekte'), p('dir', '3-е лицо', 'после e - -dir')],
        ru: 'снег продолжает идти (в новостях)',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'yaklaş', baseRu: 'приближаться', task: 'официально · он',
        context: { tr: 'Tren istasyona ___.', ru: 'Поезд приближается к станции (объявление).' },
        slots: [b('yaklaş'), m('makta', ['makta', 'mekte', 'mak'], 'официальное "сейчас"', 'последняя гласная a - -makta'), m('dır', ['dır', 'dir', 'tır'], '3-е лицо', 'в третьем лице диктор добавляет -dır; после a - -dır')],
        answer: 'yaklaşmaktadır', d: 1,
      },
      {
        kind: 'affix', base: 'devam et', baseRu: 'продолжать', task: 'официально · он',
        context: { tr: 'Kar yağmaya ___.', ru: 'Снег продолжает идти (в новостях).' },
        slots: [b('devam et'), m('mekte', ['mekte', 'makta', 'mek'], 'официальное "сейчас"', 'последняя гласная e - -mekte'), m('dir', ['dir', 'dır', 'tir'], '3-е лицо', 'после e - -dir')],
        answer: 'devam etmektedir', d: 1,
      },
      {
        kind: 'affix', base: 'beklen', baseRu: 'ожидаться', task: 'официально · он',
        context: { tr: 'Yarın yağmur ___.', ru: 'Завтра ожидается дождь (прогноз погоды).' },
        slots: [b('beklen'), m('mekte', ['mekte', 'makta', 'mek'], 'официальное "сейчас"', 'последняя гласная e - -mekte'), m('dir', ['dir', 'dır', 'tir'], '3-е лицо', 'после e - -dir')],
        answer: 'beklenmektedir', d: 1,
      },
      {
        kind: 'affix', base: 'iptal edil', baseRu: 'отменяться', task: 'официально · они',
        context: { tr: 'Kar yüzünden bazı uçuşlar ___.', ru: 'Из-за снега некоторые рейсы отменяются (в новостях).' },
        slots: [b('iptal edil'), m('mekte', ['mekte', 'makta', 'mek'], 'официальное "сейчас"', 'последняя гласная i - -mekte'), m('dir', ['dir', 'dır', 'tir'], '3-е лицо', 'у рейсов, как у вещей, хватает -dir, без -ler')],
        answer: 'iptal edilmektedir', d: 1,
      },
      {
        kind: 'affix', base: 'yağ', baseRu: 'идти (о дожде, снеге)', task: 'сейчас · в разговоре',
        context: { tr: 'Anne, duydun mu? Kar hâlâ ___!', ru: 'Мам, слышала? Снег всё ещё идёт!' },
        slots: [b('yağ'), m('ıyor', ['ıyor', 'makta', 'maktadır'], 'сейчас (-yor)', 'это разговор с мамой - только -yor; -mekte оставьте диктору')],
        answer: 'yağıyor', d: 2,
      },
      {
        kind: 'affix', base: 'çalış', baseRu: 'работать', task: 'я · сейчас · в разговоре',
        context: { tr: 'Şu an ___, sonra ararım.', ru: 'Я сейчас работаю, потом позвоню (другу).' },
        slots: [b('çalış'), m('ıyorum', ['ıyorum', 'maktayım', 'maktadır'], 'я · сейчас (-yor)', 'с другом "çalışmaktayım" звучит как канцелярское "осуществляю работу" - только -yor')],
        answer: 'çalışıyorum', d: 2,
      },
      {
        kind: 'affix', base: 'çalış', baseRu: 'работать', task: 'официально · не · он',
        context: { tr: 'Asansör şu anda ___.', ru: 'Лифт в данный момент не работает (табличка).' },
        slots: [b('çalış'), m('ma', ['ma', 'me', 'mı'], 'не', 'отрицание -ma- стоит перед -makta; последняя гласная a - -ma'), m('makta', ['makta', 'mekte', 'mak'], 'официальное "сейчас"', 'после ma - -makta'), m('dır', ['dır', 'dir', 'tır'], '3-е лицо', 'после a - -dır')],
        answer: 'çalışmamaktadır', d: 2,
      },
      {
        kind: 'transform', source: 'Kar yağmaya devam ediyor.', sourceRu: 'снег продолжает идти',
        instruction: 'Скажите, как в выпуске новостей (-mektedir)',
        answer: 'Kar yağmaya devam etmektedir.', variants: [],
        breakdown: [p('et'), p('mekte', 'официальное "сейчас"', 'после e - -mekte; t остаётся: перед согласной не смягчается'), p('dir', '3-е лицо', 'в новостях добавляют -dir')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Bazı uçuşlar iptal ediliyor.', sourceRu: 'некоторые рейсы отменяются',
        instruction: 'Скажите, как корреспондент в аэропорту (-mektedir)',
        answer: 'Bazı uçuşlar iptal edilmektedir.', variants: [],
        breakdown: [p('edil'), p('mekte', 'официальное "сейчас"', 'последняя гласная i - -mekte'), p('dir', '3-е лицо', 'в новостях добавляют -dir')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Şu an yemek yemekteyim.', sourceRu: 'сейчас я ем',
        instruction: 'Скажите это другу - по-разговорному (-yor)',
        answer: 'Şu an yemek yiyorum.', variants: ['Şimdi yemek yiyorum.', 'Yemek yiyorum.'],
        breakdown: [p('yi'), p('yorum', 'я · сейчас (-yor)', 'дома и с друзьями - только -yor; ye становится yi')],
        d: 3,
      },
    ],
  },

  // ── Урок 57: -dır «наверное» и официальное ──────────────────────────────────
  {
    rule: 'copula-dir-probability',
    explain:
      '-dır на конце - догадка, в которой говорящий почти уверен: Yorgundur - "наверное, устал", Evdedir - "наверное, дома". Гласная - по гармонии (-dır, -dir, -dur, -dür), после глухой согласной - t: yoktur. С лицом -dır встаёт после личного окончания: duymuşsundur. А в официальном тексте -dır звучит твёрдо, как печать: Müze pazartesi kapalıdır.',
    examples: [
      { parts: [p('yorgun'), p('dur', 'наверное (-dır)', 'последняя гласная u - -dur')], ru: 'наверное, устал' },
      {
        parts: [p('Bu haberi duymuş'), p('sun', 'ты', 'сначала личное окончание'), p('dur', 'наверное (-dır)', '-dır - в самом конце; после u - -dur')],
        ru: 'ты, наверное, слышал эту новость',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'yorgun', baseRu: 'усталый', task: 'наверное (-dır)',
        context: { tr: 'Bütün gün çalıştı, ___.', ru: 'Весь день работал - наверное, устал.' },
        slots: [b('yorgun'), m('dur', ['dur', 'dır', 'tur'], 'наверное (-dır)', 'последняя гласная u - -dur; n звонкая - d')],
        answer: 'yorgundur', d: 1,
      },
      {
        kind: 'affix', base: 'evde', baseRu: 'дома', task: 'наверное (-dır)',
        context: { tr: 'Ali şimdi ___.', ru: 'Али сейчас, наверное, дома.' },
        slots: [b('evde'), m('dir', ['dir', 'dır', 'tir'], 'наверное (-dır)', 'последняя гласная e - -dir')],
        answer: 'evdedir', d: 1,
      },
      {
        kind: 'affix', base: 'yok', baseRu: 'нет', task: 'наверное (-dır)',
        context: { tr: 'Parası ___.', ru: 'Наверное, у него нет денег.' },
        slots: [b('yok'), m('tur', ['tur', 'dur', 'tır'], 'наверное (-dır)', 'после глухой k - t; последняя гласная o - -tur')],
        answer: 'yoktur', d: 1,
      },
      {
        kind: 'affix', base: 'uyuyor', baseRu: 'спит', task: 'наверное (-dır)',
        context: { tr: 'Bebek şimdi ___.', ru: 'Малыш сейчас, наверное, спит.' },
        slots: [b('uyuyor'), m('dur', ['dur', 'dır', 'tur'], 'наверное (-dır)', 'после -yor - -dur')],
        answer: 'uyuyordur', d: 1,
      },
      {
        kind: 'affix', base: 'duymuş', baseRu: 'слышал', task: 'ты · наверное',
        context: { tr: 'Bu haberi ___.', ru: 'Ты, наверное, слышал эту новость.' },
        slots: [b('duymuş'), m('sun', ['sun', 'tur', 'sın'], 'ты', 'сначала личное окончание; после u - -sun'), m('dur', ['dur', 'sun', 'tur'], 'наверное (-dır)', '-dır встаёт после лица, в самом конце')],
        answer: 'duymuşsundur', d: 2,
      },
      {
        kind: 'affix', base: 'öğrenci', baseRu: 'студент', task: 'я · о себе',
        context: { tr: 'Ben ___.', ru: 'Я студент.' },
        slots: [b('öğrenci'), m('yim', ['yim', 'yimdir', 'dir'], 'я', 'о себе -dır не нужен: "öğrenciyimdir" звучит как догадка или справка')],
        answer: 'öğrenciyim', d: 2,
      },
      {
        kind: 'affix', base: 'kapalı', baseRu: 'закрыт', task: 'официально (-dır)',
        context: { tr: 'Müze pazartesi ___.', ru: 'По понедельникам музей закрыт (табличка).' },
        slots: [b('kapalı'), m('dır', ['dır', 'dir', 'tır'], 'официально (-dır)', 'на табличке -dır не сомневается; после ı - -dır')],
        answer: 'kapalıdır', d: 2,
      },
      {
        kind: 'affix', base: 'yasak', baseRu: 'запрещено', task: 'официально (-dır)',
        context: { tr: 'Sigara içmek ___.', ru: 'Курить запрещено (табличка).' },
        slots: [b('yasak'), m('tır', ['tır', 'dır', 'tir'], 'официально (-dır)', 'после глухой k - t; последняя гласная a - -tır')],
        answer: 'yasaktır', d: 2,
      },
      {
        kind: 'transform', source: 'Ali evde.', sourceRu: 'Али дома (знаю точно)',
        instruction: 'Скажите как догадку: "наверное, дома" (-dır)',
        answer: 'Ali evdedir.', variants: ['Ali herhalde evdedir.', 'Ali şimdi evdedir.'],
        breakdown: [p('evde'), p('dir', 'наверное (-dır)', 'последняя гласная e - -dir')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Şimdi çok endişeli.', sourceRu: 'сейчас он очень переживает',
        instruction: 'Скажите как догадку: "наверное, переживает" (-dır)',
        answer: 'Şimdi çok endişelidir.', variants: ['Şimdi herhalde çok endişelidir.'],
        breakdown: [p('endişeli'), p('dir', 'наверное (-dır)', 'последняя гласная i - -dir')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Onlar bu haberi duymuş.', sourceRu: 'они, говорят, слышали эту новость',
        instruction: 'Скажите "они, наверное, слышали" (-dır)',
        answer: 'Onlar bu haberi duymuşlardır.', variants: ['Bu haberi duymuşlardır.'],
        breakdown: [p('duy'), p('muş', '-mış', 'после u - -muş'), p('lar', 'они', 'сначала лицо'), p('dır', 'наверное (-dır)', 'после a - -dır')],
        d: 3,
      },
    ],
  },

  // ── Урок 58: -mesi için ─────────────────────────────────────────────────────
  {
    rule: 'purpose-mesi-icin',
    explain:
      'Если "чтобы" делает другой человек или вещь, нужна форма с хозяином: -me / -ma + притяжательное + için - gelmem için, gelmen için, gelmesi için. Хозяин стоит в родительном: Telefonun açılması için şarj et. Если исполнитель тот же - -mek için: Uyumak için telefonu kapattım.',
    examples: [
      {
        parts: [p('Telefonun açıl'), p('ma', 'действие', 'после a - -ma'), p('sı', 'его', 'чьё действие: включается телефон - telefonun'), p(' için', 'чтобы', 'için без падежа, как ev için')],
        ru: 'чтобы телефон включился',
      },
      {
        parts: [p('Senin anla'), p('ma', 'действие', 'после a - -ma'), p('n', 'твоё', 'понимаешь ты, а говорю я'), p(' için', 'чтобы')],
        ru: 'чтобы ты понял',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'uyu', baseRu: 'спать', task: 'чтобы он',
        context: { tr: 'Bebeğin ___ için ışığı kapattım.', ru: 'Я выключил свет, чтобы малыш уснул.' },
        slots: [b('uyu'), m('ma', ['ma', 'me', 'mu'], 'действие', 'последняя гласная u - -ma'), m('sı', ['sı', 'n', 'm'], 'его', 'спит малыш - bebeğin, значит, "его"')],
        answer: 'uyuması', d: 1,
      },
      {
        kind: 'affix', base: 'anla', baseRu: 'понимать', task: 'чтобы ты',
        context: { tr: 'Senin ___ için yavaş konuşuyorum.', ru: 'Я говорю медленно, чтобы ты понял.' },
        slots: [b('anla'), m('ma', ['ma', 'me', 'mak'], 'действие', 'после a - -ma'), m('n', ['n', 'm', 'sı'], 'твоё', 'понимаешь ты - senin')],
        answer: 'anlaman', d: 1,
      },
      {
        kind: 'affix', base: 'yüklen', baseRu: 'установиться', task: 'чтобы оно',
        context: { tr: 'Güncellemenin ___ için telefonu yeniden başlatın.', ru: 'Чтобы обновление установилось, перезагрузите телефон.' },
        slots: [b('yüklen'), m('me', ['me', 'ma', 'mek'], 'действие', 'последняя гласная e - -me'), m('si', ['si', 'sı', 'n'], 'его', 'устанавливается обновление - güncellemenin; после e - -si')],
        answer: 'yüklenmesi', d: 1,
      },
      {
        kind: 'affix', base: 'gir', baseRu: 'входить', task: 'чтобы мы',
        context: { tr: 'Bizim ___ için kapıyı açık bıraktı.', ru: 'Он оставил дверь открытой, чтобы мы вошли.' },
        slots: [b('gir'), m('me', ['me', 'ma', 'mek'], 'действие', 'последняя гласная i - -me'), m('miz', ['miz', 'niz', 'si'], 'наше', 'войдём мы - bizim')],
        answer: 'girmemiz', d: 1,
      },
      {
        kind: 'affix', base: 'uyu', baseRu: 'спать', task: 'чтобы (сам)',
        context: { tr: 'İyi ___ için telefonu kapattım.', ru: 'Чтобы хорошо выспаться, я выключил телефон.' },
        slots: [b('uyu'), m('mak', ['mak', 'mam', 'ması'], 'словарная форма', 'выключил я и сплю я - исполнитель тот же, значит, -mak için')],
        answer: 'uyumak', d: 2,
      },
      {
        kind: 'affix', base: 'açıl', baseRu: 'включиться', task: 'чтобы он (телефон)',
        context: { tr: 'Telefonun ___ için şarj etmen lazım.', ru: 'Чтобы телефон включился, тебе нужно его зарядить.' },
        slots: [b('açıl'), m('ma', ['ma', 'mak', 'me'], 'действие', 'заряжаешь ты, включается телефон - исполнители разные, не -mak'), m('sı', ['sı', 'sının', 'n'], 'его', 'için после -ması без падежа: не "açılmasının için"')],
        answer: 'açılması', d: 2,
      },
      {
        kind: 'affix', base: 'gör', baseRu: 'видеть', task: 'чтобы они не',
        context: { tr: 'Çocukların ___ için şifre koydum.', ru: 'Я поставил пароль, чтобы дети не видели.' },
        slots: [b('gör'), m('me', ['me', 'ma', 'mi'], 'не', 'отрицание - внутри слова, сразу после основы'), m('me', ['me', 'mek', 'ma'], 'действие', 'после e - -me'), m('si', ['si', 'n', 'sı'], 'их', 'у "детей" окончание обычно как у "его" - -si; после e - -si')],
        answer: 'görmemesi', d: 2,
      },
      {
        kind: 'transform', source: 'Чтобы телефон включился, его надо зарядить.',
        instruction: 'Скажите это по-турецки',
        answer: 'Telefonun açılması için şarj etmen lazım.', variants: ['Telefonun açılması için şarj etmelisin.'],
        breakdown: [p('açıl'), p('ma', 'действие', 'включается телефон, а заряжаешь ты - нужна форма с хозяином'), p('sı', 'его', 'хозяин - telefonun'), p(' için', 'чтобы', 'без падежа')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Я выключил музыку, чтобы малыш уснул.',
        instruction: 'Скажите это по-турецки',
        answer: 'Bebeğin uyuması için müziği kapattım.', variants: [],
        breakdown: [p('uyu'), p('ma', 'действие', 'после u - -ma'), p('sı', 'его', 'спит малыш - bebeğin'), p(' için', 'чтобы', 'без падежа')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Fotoğraflar kaybolmasın diye onları başka bir yerde de saklayın.', sourceRu: 'храните фотографии ещё где-нибудь, чтобы они не пропали',
        instruction: 'Скажите, как в инструкции (-mesi için)',
        answer: 'Fotoğrafların kaybolmaması için onları başka bir yerde de saklayın.', variants: [],
        breakdown: [p('kaybol'), p('ma', 'не', 'отрицание - сразу после основы'), p('ma', 'действие', 'после a - -ma'), p('sı', 'их', 'хозяин - fotoğrafların, окончание как у "его"'), p(' için', 'чтобы')],
        d: 3,
      },
    ],
  },

  // ── Урок 60: -dığından emin; -ıp -madığını kontrol etmek ────────────────────
  {
    rule: 'noun-clause-emin',
    explain:
      'emin olmak спрашивает "в чём?" исходным падежом (bundan eminim), поэтому и целое событие с -dığı и лицом встаёт в -den: kilitlediğimden eminim. kontrol etmek спрашивает "что?" - винительный: пара "сделал - не сделал" kilitleyip kilitlemediğini kontrol et.',
    examples: [
      {
        parts: [p('Kapıyı kilitle'), p('diğ', '"то, что" (-dığı)', 'k перед гласной - ğ'), p('im', 'я'), p('den', 'исходный', 'emin olmak - "в чём?", как bundan'), p(' eminim')],
        ru: 'я уверен, что запер дверь',
      },
      {
        parts: [p('Kodun gelip gel'), p('me', 'не'), p('diğ', '-dığı'), p('i', 'его'), p('ni', 'винительный', 'kontrol etmek - "что?", как bunu'), p(' kontrol et')],
        ru: 'проверь, пришёл ли код',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'kilitlediğim', baseRu: 'то, что я запер', task: 'уверен, что...',
        context: { tr: 'Kapıyı ___ eminim.', ru: 'Я уверен, что запер дверь.' },
        slots: [b('kilitlediğim'), m('den', ['den', 'i', 'e'], 'исходный падеж', 'emin olmak - "в чём?", как bundan eminim')],
        answer: 'kilitlediğimden', d: 1,
      },
      {
        kind: 'affix', base: 'yazdığın', baseRu: 'то, что ты ввёл', task: 'уверен, что...',
        context: { tr: 'Şifreyi doğru ___ emin misin?', ru: 'Ты уверен, что правильно ввёл пароль?' },
        slots: [b('yazdığın'), m('dan', ['dan', 'ı', 'a'], 'исходный падеж', 'emin olmak - "в чём?"; после a - -dan')],
        answer: 'yazdığından', d: 1,
      },
      {
        kind: 'affix', base: 'geleceğin', baseRu: 'то, что ты придёшь', task: 'уверен, что... (будущее)',
        context: { tr: 'Yarın ___ eminim.', ru: 'Я уверен, что ты завтра придёшь.' },
        slots: [b('geleceğin'), m('den', ['den', 'i', 'e'], 'исходный падеж', 'о будущем - -eceğinden, падеж тот же')],
        answer: 'geleceğinden', d: 1,
      },
      {
        kind: 'affix', base: 'gelip gelmediğ', baseRu: 'пришёл или нет', task: 'проверить, ... ли',
        context: { tr: 'Kodun ___ kontrol edin.', ru: 'Проверьте, пришёл ли код.' },
        slots: [b('gelip gelmediğ'), m('i', ['i', 'im', 'in'], 'его', 'пришёл код - kodun'), m('ni', ['ni', 'nden', 'ne'], 'винительный', 'kontrol etmek - "что?", значит, -i')],
        answer: 'gelip gelmediğini', d: 1,
      },
      {
        kind: 'affix', base: 'kilitleyip kilitlemediğ', baseRu: 'запер или нет', task: 'проверь, ... ли',
        context: { tr: 'Kapıyı ___ kontrol et.', ru: 'Проверь, запер ли ты дверь.' },
        slots: [b('kilitleyip kilitlemediğ'), m('in', ['in', 'im', 'i'], 'твоё', 'запирал ты'), m('i', ['i', 'den', 'e'], 'винительный', 'kontrol etmek - "что?": не "...mediğinden kontrol et"')],
        answer: 'kilitleyip kilitlemediğini', d: 2,
      },
      {
        kind: 'affix', base: 'gönderip göndermediğim', baseRu: 'отправил я или нет', task: 'не уверен, ... ли',
        context: { tr: 'Kodu ___ emin değilim.', ru: 'Не уверен, отправил ли я код.' },
        slots: [b('gönderip göndermediğim'), m('den', ['den', 'i', 'e'], 'исходный падеж', 'emin değil - тоже "в чём?", -den')],
        answer: 'gönderip göndermediğimden', d: 2,
      },
      {
        kind: 'affix', base: 'güvenli olup olmadığ', baseRu: 'безопасна или нет', task: 'проверить, ... ли',
        context: { tr: 'Bağlantının ___ kontrol edin.', ru: 'Проверьте, безопасна ли ссылка.' },
        slots: [b('güvenli olup olmadığ'), m('ı', ['ı', 'ın', 'ım'], 'его', 'хозяин - bağlantının'), m('nı', ['nı', 'ndan', 'na'], 'винительный', 'kontrol etmek - "что?"; с именем - olup olmadığını')],
        answer: 'güvenli olup olmadığını', d: 2,
      },
      {
        kind: 'transform', source: 'Я уверен, что поменял пароль.',
        instruction: 'Скажите это по-турецки',
        answer: 'Şifreyi değiştirdiğimden eminim.', variants: ['Ben şifreyi değiştirdiğimden eminim.'],
        breakdown: [p('değiştir'), p('diğ', '"то, что" (-dığı)', 'k перед гласной - ğ'), p('im', 'я'), p('den', 'исходный', 'emin olmak - "в чём?"')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Проверь, пришёл ли код.',
        instruction: 'Скажите это по-турецки',
        answer: 'Kodun gelip gelmediğini kontrol et.', variants: [],
        breakdown: [p('gelip gel'), p('me', 'не', 'пара "сделал - не сделал" вместо русского "ли"'), p('diğ', '-dığı'), p('i', 'его'), p('ni', 'винительный', 'kontrol etmek - "что?"')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Я уверен, что он придёт.',
        instruction: 'Скажите это по-турецки',
        answer: 'Geleceğinden eminim.', variants: ['Onun geleceğinden eminim.'],
        breakdown: [p('gel'), p('eceğ', 'будущее (-ecek)', 'k перед гласной - ğ'), p('i', 'его'), p('nden', 'исходный', 'emin olmak - "в чём?"; после "его" - n')],
        blankWhole: true, d: 3,
      },
    ],
  },

  // ── Урок 62: -mek yerine ────────────────────────────────────────────────────
  {
    rule: 'instead-mek-yerine',
    explain:
      '"Вместо того чтобы..." - -mek yerine: глагол в словарной форме и сразу после него yerine. Araba kullanmak yerine yürüyün. Первый глагол не меняется никогда, лицо и время - только в конце; için не нужен: "чтобы" уже спрятано в yerine.',
    examples: [
      {
        parts: [p('Araba kullan'), p('mak', 'словарная форма', 'глагол на -mek ведёт себя как имя'), p(' yerine', 'вместо', 'yer "место" - "на месте езды"'), p(' yürüyün.')],
        ru: 'вместо того чтобы ездить на машине, ходите пешком',
      },
      { parts: [p('çay'), p(' yerine', 'вместо', 'с именем - та же схема'), p(' su')], ru: 'вместо чая - воду' },
    ],
    items: [
      {
        kind: 'affix', base: 'kullan', baseRu: 'водить, использовать', task: 'вместо того чтобы',
        context: { tr: 'Araba ___ yürüyün.', ru: 'Вместо того чтобы ездить на машине, ходите пешком.' },
        slots: [b('kullan'), m('mak yerine', ['mak yerine', 'maya yerine', 'mak için yerine'], 'вместо того чтобы', 'словарная форма и сразу yerine, без için и без -ya')],
        answer: 'kullanmak yerine', d: 1,
      },
      {
        kind: 'affix', base: 'al', baseRu: 'брать, покупать', task: 'вместо того чтобы',
        context: { tr: 'Plastik poşet ___ kendi çantamı kullanıyorum.', ru: 'Вместо того чтобы брать пластиковый пакет, я пользуюсь своей сумкой.' },
        slots: [b('al'), m('mak yerine', ['mak yerine', 'mek yerine', 'maya yerine'], 'вместо того чтобы', 'после a - -mak; сразу yerine')],
        answer: 'almak yerine', d: 1,
      },
      {
        kind: 'affix', base: 'israf et', baseRu: 'тратить впустую', task: 'вместо того чтобы',
        context: { tr: 'Kâğıt ___ ekranda okuyalım.', ru: 'Давайте читать с экрана, а не тратить бумагу впустую.' },
        slots: [b('israf et'), m('mek yerine', ['mek yerine', 'mak yerine', 'mek için yerine'], 'вместо того чтобы', 'после e - -mek; için не нужен')],
        answer: 'israf etmek yerine', d: 1,
      },
      {
        kind: 'affix', base: 'at', baseRu: 'выбрасывать', task: 'вместо того чтобы',
        context: { tr: 'Şişeleri çöpe ___ geri dönüştürüyoruz.', ru: 'Вместо того чтобы выбрасывать бутылки, мы их перерабатываем.' },
        slots: [b('at'), m('mak yerine', ['mak yerine', 'ıyoruz yerine', 'mek yerine'], 'вместо того чтобы', 'первый глагол не меняется: не "atıyoruz yerine"; лицо - только в конце')],
        answer: 'atmak yerine', d: 2,
      },
      {
        kind: 'affix', base: 'git', baseRu: 'идти', task: 'вместо того чтобы ты',
        context: { tr: 'Senin ___ ben gideyim.', ru: 'Чем тебе идти, лучше пойду я.' },
        slots: [b('git'), m('men yerine', ['men yerine', 'mek yerine', 'mene yerine'], 'вместо того чтобы ты', 'делает другой человек - -mesi с лицом: gitmen yerine, без падежа')],
        answer: 'gitmen yerine', d: 2,
      },
      {
        kind: 'affix', base: 'kullan', baseRu: 'водить, использовать', task: 'вместо того чтобы · вчера',
        context: { tr: 'Dün araba ___ yürüdüm.', ru: 'Вчера я не поехал на машине, а пошёл пешком.' },
        slots: [b('kullan'), m('mak', ['mak', 'dım', 'ıyorum'], 'словарная форма', 'время - только в конце (yürüdüm), первый глагол не меняется'), m(' yerine', [' yerine', ' için', ' kadar'], 'вместо', '"вместо того чтобы" - yerine')],
        answer: 'kullanmak yerine', d: 2,
      },
      {
        kind: 'transform', source: 'Вместо того чтобы ездить на машине, я хожу пешком.',
        instruction: 'Скажите это по-турецки',
        answer: 'Araba kullanmak yerine yürüyorum.', variants: ['Araba sürmek yerine yürüyorum.', 'Araba kullanmak yerine yürürüm.'],
        breakdown: [p('kullan'), p('mak', 'словарная форма', 'первый глагол не меняется'), p(' yerine', 'вместо')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Şişeleri çöpe atmayalım, geri dönüşüm kutusuna atalım.', sourceRu: 'давай не выбрасывать бутылки в мусор, а класть в контейнер для переработки',
        instruction: 'Скажите одной фразой с -mek yerine',
        answer: 'Şişeleri çöpe atmak yerine geri dönüşüm kutusuna atalım.', variants: [],
        breakdown: [p('at'), p('mak', 'словарная форма', 'после a - -mak'), p(' yerine', 'вместо', 'часть с yerine - первой')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Вместо того чтобы пить чай, выпей воды.',
        instruction: 'Скажите это по-турецки',
        answer: 'Çay içmek yerine su iç.', variants: [],
        breakdown: [p('iç'), p('mek', 'словарная форма', 'после i - -mek'), p(' yerine', 'вместо')],
        blankWhole: true, d: 3,
      },
    ],
  },

  // ── Урок 63: -dığı sürece ───────────────────────────────────────────────────
  {
    rule: 'conditional-digi-surece',
    explain:
      '-dığı sürece - "пока; при условии, что": Doğayı koruduğumuz sürece orman yaşar. Глагол берёт -dığ- с лицом (koruduğum, koruduğumuz...), следом идёт sürece; время решает только конец фразы. Не путайте три "пока": -ken - в то же время, -ene kadar - "пока не", -dığı sürece - при условии.',
    examples: [
      {
        parts: [p('Doğayı koru'), p('duğ', '-dığı', 'после u - -duğ'), p('umuz', 'мы'), p(' sürece', 'пока, при условии', 'условие держится всё время'), p(' orman yaşar.')],
        ru: 'пока мы бережём природу, лес живёт',
      },
      {
        parts: [p('Yağmur yağ'), p('ma', 'не'), p('dığ', '-dığı'), p('ı', 'его'), p(' sürece', 'пока (не)')],
        ru: 'если не пойдёт дождь',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'koru', baseRu: 'беречь', task: 'пока мы',
        context: { tr: 'Doğayı ___ sürece orman yaşar.', ru: 'Пока мы бережём природу, лес живёт.' },
        slots: [b('koru'), m('duğ', ['duğ', 'dığ', 'tuğ'], '-dığı', 'последняя гласная u - -duğ; k перед гласной - ğ'), m('umuz', ['umuz', 'um', 'un'], 'мы', 'бережём мы')],
        answer: 'koruduğumuz', d: 1,
      },
      {
        kind: 'affix', base: 'ol', baseRu: 'быть', task: 'пока ты',
        context: { tr: 'Sen yanımda ___ sürece korkmuyorum.', ru: 'Пока ты рядом, я не боюсь.' },
        slots: [b('ol'), m('duğ', ['duğ', 'dığ', 'tuğ'], '-dığı', 'с именем помогает olmak; после o - -duğ'), m('un', ['un', 'um', 'u'], 'ты', 'рядом ты - sen')],
        answer: 'olduğun', d: 1,
      },
      {
        kind: 'affix', base: 'at', baseRu: 'бросать', task: 'пока они',
        context: { tr: 'İnsanlar çöp ___ sürece orman temiz kalmaz.', ru: 'Пока люди бросают мусор, лес чистым не останется.' },
        slots: [b('at'), m('tık', ['tık', 'dık', 'tığ'], '-dığı', 'после глухой t - t; перед -ları k не смягчается'), m('ları', ['ları', 'ı', 'larını'], 'они', 'у "они" - -dıkları')],
        answer: 'attıkları', d: 1,
      },
      {
        kind: 'affix', base: 'yağ', baseRu: 'идти (о дожде)', task: 'пока не',
        context: { tr: 'Yağmur ___ sürece çıkarız.', ru: 'Если не пойдёт дождь, выйдем.' },
        slots: [b('yağ'), m('ma', ['ma', 'me', 'mı'], 'не', 'после a - -ma'), m('dığ', ['dığ', 'diğ', 'tığ'], '-dığı', 'после a - -dığ'), m('ı', ['ı', 'i', 'ın'], 'его', 'идёт дождь - "его"')],
        answer: 'yağmadığı', d: 1,
      },
      {
        kind: 'affix', base: 'gel', baseRu: 'приходить', task: 'пока не (до момента)',
        context: { tr: 'Sen ___ beklerim.', ru: 'Подожду, пока ты не придёшь.' },
        slots: [b('gel'), m('ene kadar', ['ene kadar', 'diğin sürece', 'irken'], 'до момента', '"пока не" = до момента - -ene kadar; -dığı sürece - "при условии"')],
        answer: 'gelene kadar', d: 2,
      },
      {
        kind: 'affix', base: 'ol', baseRu: 'быть', task: 'пока (при условии)',
        context: { tr: 'Hava güzel ___ ben varım.', ru: 'Пока погода хорошая, я в деле.' },
        slots: [b('ol'), m('duğu sürece', ['duğu sürece', 'acağı sürece', 'ana kadar'], 'при условии', 'условие держится всё время - -dığı sürece; будущего внутри нет')],
        answer: 'olduğu sürece', d: 2,
      },
      {
        kind: 'affix', base: 'koru', baseRu: 'беречь', task: 'пока мы · о будущем',
        context: { tr: 'Ormanı ___ sürece kuşlar yaşayacak.', ru: 'Пока мы будем беречь лес, птицы будут жить.' },
        slots: [b('koru'), m('duğumuz', ['duğumuz', 'yacağımız', 'yoruz'], '-dığı · мы', 'часть с sürece времени не несёт: не "koruyacağımız sürece"; будущее - в конце, yaşayacak')],
        answer: 'koruduğumuz', d: 2,
      },
      {
        kind: 'transform', source: 'Ormanı korursak kuşlar yaşar.', sourceRu: 'если будем беречь лес, птицы будут жить',
        instruction: 'Скажите "пока будем беречь" - всё время (-dığı sürece)',
        answer: 'Ormanı koruduğumuz sürece kuşlar yaşar.', variants: [],
        breakdown: [p('koru'), p('duğ', '-dığı', 'после u - -duğ'), p('umuz', 'мы'), p(' sürece', 'пока, при условии')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Пока у меня есть время, я буду работать волонтёром.',
        instruction: 'Скажите это по-турецки',
        answer: 'Zamanım olduğu sürece gönüllü olarak çalışırım.', variants: [],
        breakdown: [p('ol'), p('duğ', '-dığı', 'с именем помогает olmak'), p('u', 'его'), p(' sürece', 'пока, при условии')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Пока ты здесь, я спокоен.',
        instruction: 'Скажите это по-турецки',
        answer: 'Sen burada olduğun sürece rahatım.', variants: ['Burada olduğun sürece rahatım.'],
        breakdown: [p('ol'), p('duğ', '-dığı', 'с именем помогает olmak'), p('un', 'ты'), p(' sürece', 'пока, при условии')],
        blankWhole: true, d: 3,
      },
    ],
  },

  // ── Урок 66: -sın / -sınlar «пусть» ─────────────────────────────────────────
  {
    rule: 'imperative-3rd-sin',
    explain:
      '"Пусть он..." - окончание прямо на основе глагола, без -ir и без y: bekle-sin, doldur-sun, imzala-sın, gel-sin. Для "они" - -sınlar, "пусть не" - -masın / -mesin. Не путайте: gelirsin - "ты придёшь", а gelsin - "пусть ОН придёт".',
    examples: [
      { parts: [p('Biraz bekle'), p('sin', 'пусть он', 'последняя гласная e - -sin')], ru: 'пусть немного подождёт' },
      { parts: [p('Formu doldur'), p('sun', 'пусть', 'после u - -sun'), p('lar', 'они')], ru: 'пусть заполнят бланк' },
    ],
    items: [
      {
        kind: 'affix', base: 'bekle', baseRu: 'ждать', task: 'пусть он',
        context: { tr: 'Biraz ___.', ru: 'Пусть немного подождёт.' },
        slots: [b('bekle'), m('sin', ['sin', 'sın', 'sun'], 'пусть он', 'последняя гласная e - -sin')],
        answer: 'beklesin', d: 1,
      },
      {
        kind: 'affix', base: 'doldur', baseRu: 'заполнять', task: 'пусть они',
        context: { tr: 'Formu ___.', ru: 'Пусть они заполнят бланк.' },
        slots: [b('doldur'), m('sun', ['sun', 'sın', 'sün'], 'пусть', 'последняя гласная u - -sun'), m('lar', ['lar', 'ler', 'sın'], 'они', 'после u - -lar')],
        answer: 'doldursunlar', d: 1,
      },
      {
        kind: 'affix', base: 'imzala', baseRu: 'подписывать', task: 'пусть он',
        context: { tr: 'Dilekçeyi kendisi ___.', ru: 'Пусть заявление подпишет сам.' },
        slots: [b('imzala'), m('sın', ['sın', 'sin', 'sun'], 'пусть он', 'последняя гласная a - -sın')],
        answer: 'imzalasın', d: 1,
      },
      {
        kind: 'affix', base: 'ara', baseRu: 'звонить', task: 'пусть не',
        context: { tr: 'Kimse beni ___.', ru: 'Пусть мне никто не звонит.' },
        slots: [b('ara'), m('ma', ['ma', 'me', 'mı'], 'не', 'после a - -ma'), m('sın', ['sın', 'sin', 'sun'], 'пусть', 'после a - -sın')],
        answer: 'aramasın', d: 1,
      },
      {
        kind: 'affix', base: 'Kolay gel', baseRu: 'легко идти', task: 'пусть (пожелание)',
        context: { tr: '___! - Sağ ol.', ru: 'Лёгкой работы! (дословно: пусть легко идёт) - Спасибо.' },
        slots: [b('Kolay gel'), m('sin', ['sin', 'sen', 'in'], 'пусть', 'пожелание - тоже "пусть": gelsin')],
        answer: 'Kolay gelsin', d: 2,
      },
      {
        kind: 'affix', base: 'bekle', baseRu: 'ждать', task: 'пусть он (о посетителе)',
        context: { tr: '- Ziyaretçi geldi, girsin mi? - Hayır, ___.', ru: '- Посетитель пришёл, пусть войдёт? - Нет, пусть подождёт.' },
        slots: [b('bekle'), m('sin', ['sin', 'rsin', 'yin'], 'пусть он', 'о посетителе - третье лицо; beklersin - "ты подождёшь", bekleyin - "подождите"')],
        answer: 'beklesin', d: 2,
      },
      {
        kind: 'affix', base: 'gel', baseRu: 'приходить', task: 'пусть он',
        context: { tr: 'Bugün olmaz, yarın ___.', ru: 'Сегодня нельзя, пусть придёт завтра.' },
        slots: [b('gel'), m('sin', ['sin', 'irsin', 'sen'], 'пусть он', 'gelirsin - "ты придёшь", gelsen - "если бы ты пришёл"; "пусть он" - gelsin')],
        answer: 'gelsin', d: 2,
      },
      {
        kind: 'affix', base: 'unut', baseRu: 'забывать', task: 'пусть не',
        context: { tr: 'Tarihi de yazmayı ___!', ru: 'Пусть не забудет поставить и дату!' },
        slots: [b('unut'), m('ma', ['ma', 'mu', 'me'], 'не', 'отрицание - -ma, у него только два варианта'), m('sın', ['sın', 'sun', 'sin'], 'пусть', 'после ma - -sın')],
        answer: 'unutmasın', d: 2,
      },
      {
        kind: 'transform', source: 'Bekle!', sourceRu: 'подожди! (так вы скажете самой секретарше)',
        instruction: 'Скажите о посетителе: "пусть подождёт"',
        answer: 'Beklesin.', variants: [],
        breakdown: [p('Bekle'), p('sin', 'пусть он', 'о третьем человеке - -sin, а не "Bekle!"')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Evrakları bana getir.', sourceRu: 'принеси мне документы',
        instruction: 'Скажите о сотрудниках: "пусть принесут" (-sınlar)',
        answer: 'Evrakları bana getirsinler.', variants: ['Evrakları bana getirsin.'],
        breakdown: [p('getir'), p('sin', 'пусть', 'последняя гласная i - -sin'), p('ler', 'они', 'после i - -ler')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Пусть дети войдут внутрь.',
        instruction: 'Скажите это по-турецки',
        answer: 'Çocuklar içeri girsin.', variants: ['Çocuklar içeri girsinler.'],
        breakdown: [p('gir'), p('sin', 'пусть', 'в разговоре для "они" часто хватает -sin')],
        blankWhole: true, d: 3,
      },
    ],
  },

  // ── Урок 67: -ecek kadar ────────────────────────────────────────────────────
  {
    rule: 'degree-ecek-kadar',
    explain:
      '"Хватает на...", "настолько, чтобы..." - глагол в форме будущего -ecek / -acak плюс kadar: Ev alacak kadar param yok - "у меня нет столько денег, чтобы купить дом". Лица на -ecek здесь нет: чьи деньги, показывает param, paran. Не путайте с -ene kadar - "пока не".',
    examples: [
      {
        parts: [p('Ev al'), p('acak', 'будущее', 'после a - -acak'), p(' kadar', 'столько, чтобы', 'сначала то, на что хватает'), p(' param yok.')],
        ru: 'у меня нет столько денег, чтобы купить дом',
      },
      { parts: [p('Herkese yet'), p('ecek', 'будущее', 'после e - -ecek'), p(' kadar', 'столько, чтобы'), p(' yemek var.')], ru: 'еды хватит на всех' },
    ],
    items: [
      {
        kind: 'affix', base: 'al', baseRu: 'покупать', task: 'хватает на...',
        context: { tr: 'Bir kahve ___ kadar param var.', ru: 'Денег у меня хватит на один кофе.' },
        slots: [b('al'), m('acak', ['acak', 'ecek', 'ana'], 'будущее', 'после a - -acak; -ana kadar - это "пока не"')],
        answer: 'alacak', d: 1,
      },
      {
        kind: 'affix', base: 'yet', baseRu: 'хватать', task: 'хватает на...',
        context: { tr: 'Herkese ___ kadar yemek var.', ru: 'Еды хватит на всех.' },
        slots: [b('yet'), m('ecek', ['ecek', 'acak', 'ene'], 'будущее', 'после e - -ecek')],
        answer: 'yetecek', d: 1,
      },
      {
        kind: 'affix', base: 'öde', baseRu: 'платить', task: 'хватает на...',
        context: { tr: 'Bu faturayı ___ kadar param yok.', ru: 'У меня не хватает денег, чтобы оплатить этот счёт.' },
        slots: [b('öde'), m('yecek', ['yecek', 'yacak', 'yene'], 'будущее', 'после гласной - y; последняя гласная e - -yecek')],
        answer: 'ödeyecek', d: 1,
      },
      {
        kind: 'affix', base: 'duy', baseRu: 'слышать', task: 'так, что не...',
        context: { tr: 'Kimse ___ kadar sessiz konuştu.', ru: 'Он говорил так тихо, что никто не слышал.' },
        slots: [b('duy'), m('ma', ['ma', 'me', 'mu'], 'не', 'после u - -ma'), m('yacak', ['yacak', 'yecek', 'yana'], 'будущее', 'после гласной - y; после a - -yacak')],
        answer: 'duymayacak', d: 1,
      },
      {
        kind: 'affix', base: 'bit', baseRu: 'кончаться', task: 'пока не (до момента)',
        context: { tr: 'Para ___ kadar harcadım.', ru: 'Я тратил, пока деньги не кончились.' },
        slots: [b('bit'), m('ene', ['ene', 'ecek', 'tiği'], 'до момента', '"пока не" - это -ene kadar (урок 48), а не -ecek kadar')],
        answer: 'bitene', d: 2,
      },
      {
        kind: 'affix', base: 'gid', baseRu: 'ехать (git-)', task: 'хватает на...',
        context: { tr: 'Tatile ___ kadar param var.', ru: 'Денег хватит на отпуск.' },
        slots: [b('gid'), m('ecek', ['ecek', 'ene', 'eceğim'], 'будущее', '"хватит на" - голое -ecek kadar, без лица: не "gideceğim kadar"')],
        answer: 'gidecek', d: 2,
      },
      {
        kind: 'affix', base: 'taşı', baseRu: 'нести', task: 'так, что невозможно',
        context: { tr: 'Bu çanta ___ kadar ağır.', ru: 'Эта сумка такая тяжёлая, что её не унести.' },
        slots: [b('taşı'), m('yama', ['yama', 'ama', 'yeme'], 'не сможет', '"невозможно" - -eme-; после гласной - y; после a - -yama'), m('yacak', ['yacak', 'cak', 'yecek'], 'будущее', 'после гласной - y; после a - -yacak')],
        answer: 'taşıyamayacak', d: 2,
      },
      {
        kind: 'transform', source: 'У меня нет столько денег, чтобы купить машину.',
        instruction: 'Скажите это по-турецки',
        answer: 'Araba alacak kadar param yok.', variants: [],
        breakdown: [p('al'), p('acak', 'будущее', 'после a - -acak'), p(' kadar', 'столько, чтобы')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Ev almak için yeterli param yok.', sourceRu: 'у меня недостаточно денег, чтобы купить дом',
        instruction: 'Скажите по-разговорному, с -ecek kadar',
        answer: 'Ev alacak kadar param yok.', variants: [],
        breakdown: [p('al'), p('acak', 'будущее', 'после a - -acak'), p(' kadar', 'столько, чтобы')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Хлеба хватит на всех.',
        instruction: 'Скажите это по-турецки',
        answer: 'Herkese yetecek kadar ekmek var.', variants: [],
        breakdown: [p('yet'), p('ecek', 'будущее', 'после e - -ecek'), p(' kadar', 'столько, чтобы')],
        blankWhole: true, d: 3,
      },
    ],
  },

  // ── Урок 70: -mesine izin vermek; -mesi yasak ───────────────────────────────
  {
    rule: 'permission-mesine-izin',
    explain:
      '"Разрешить кому-то что-то сделать" - -mesine izin vermek: Polis gitmemize izin verdi. izin vermek спрашивает "чему?", поэтому в конце дательный -e; кто - уже внутри слова, bana не нужно. -mesi yasak - запрет лично кому-то, без падежа: Burada sigara içmeniz yasak.',
    examples: [
      {
        parts: [p('Annem git'), p('me', 'действие'), p('m', 'моё', 'кто - уже внутри слова, bana не нужно'), p('e', 'дательный', 'izin vermek - "чему?"'), p(' izin verdi.')],
        ru: 'мама разрешила мне пойти',
      },
      {
        parts: [p('Burada sigara iç'), p('me', 'действие'), p('niz', 'ваше'), p(' yasak', 'нельзя', 'после yasak - без падежа')],
        ru: 'вам здесь курить нельзя',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'git', baseRu: 'идти', task: 'разрешить мне',
        context: { tr: 'Annem ___ izin verdi.', ru: 'Мама разрешила мне пойти.' },
        slots: [b('git'), m('me', ['me', 'mek', 'ma'], 'действие', 'после i - -me'), m('m', ['m', 'n', 'si'], 'моё', 'иду я'), m('e', ['e', 'i', 'den'], 'дательный', 'izin vermek - "чему?"')],
        answer: 'gitmeme', d: 1,
      },
      {
        kind: 'affix', base: 'gir', baseRu: 'входить', task: 'не разрешить нам',
        context: { tr: 'Polis ___ izin vermedi.', ru: 'Полиция нас не пустила.' },
        slots: [b('gir'), m('me', ['me', 'mek', 'ma'], 'действие', 'после i - -me'), m('miz', ['miz', 'niz', 'm'], 'наше', 'входили мы'), m('e', ['e', 'i', 'de'], 'дательный', 'izin vermek - "чему?"; отрицание - в izin vermedi')],
        answer: 'girmemize', d: 1,
      },
      {
        kind: 'affix', base: 'iç', baseRu: 'пить, курить', task: 'вам нельзя',
        context: { tr: 'Burada sigara ___ yasak.', ru: 'Вам здесь курить нельзя.' },
        slots: [b('iç'), m('me', ['me', 'ma', 'mek'], 'действие', 'после i - -me'), m('niz', ['niz', 'nize', 'nizi'], 'ваше', 'после yasak - без падежа')],
        answer: 'içmeniz', d: 1,
      },
      {
        kind: 'affix', base: 'gir', baseRu: 'входить', task: 'им нельзя',
        context: { tr: 'Çocukların ___ yasak.', ru: 'Детям вход запрещён.' },
        slots: [b('gir'), m('me', ['me', 'ma', 'mek'], 'действие', 'после i - -me'), m('si', ['si', 'sine', 'n'], 'их', 'у "детей" окончание обычно как у "его"; yasak - без падежа')],
        answer: 'girmesi', d: 1,
      },
      {
        kind: 'affix', base: 'gelmen', baseRu: 'твой приход', task: 'разрешаю тебе',
        context: { tr: 'Yarın ___ izin veriyorum.', ru: 'Разрешаю тебе прийти завтра.' },
        slots: [b('gelmen'), m('e', ['e', 'i', 'den'], 'дательный', 'izin vermek - "чему?", дательный; gelmeni - для istemek')],
        answer: 'gelmene', d: 2,
      },
      {
        kind: 'affix', base: 'gelmen', baseRu: 'твой приход', task: 'хочу, чтобы ты',
        context: { tr: 'Yarın ___ istiyorum.', ru: 'Я хочу, чтобы ты пришёл завтра.' },
        slots: [b('gelmen'), m('i', ['i', 'e', 'den'], 'винительный', 'istemek - "что?", винительный (урок 43); дательный - для izin vermek')],
        answer: 'gelmeni', d: 2,
      },
      {
        kind: 'affix', base: 'bekle', baseRu: 'ждать', task: 'не разрешить вам',
        context: { tr: 'Maalesef burada ___ izin veremem.', ru: 'К сожалению, ждать здесь я вам не разрешу.' },
        slots: [b('bekle'), m('me', ['me', 'ma', 'mek'], 'действие', 'после e - -me'), m('nize', ['nize', 'nizi', 'niz'], 'ваше + дательный', 'izin vermek - "чему?": -nize')],
        answer: 'beklemenize', d: 2,
      },
      {
        kind: 'affix', base: 'park et', baseRu: 'парковаться', task: 'вам нельзя',
        context: { tr: 'İyi akşamlar. Buraya ___ yasak.', ru: 'Добрый вечер. Сюда вам парковаться нельзя.' },
        slots: [b('park et'), m('me', ['me', 'ma', 'mek'], 'действие', 'после e - -me'), m('niz', ['niz', 'nize', 'nizi'], 'ваше', 'yasak - без падежа')],
        answer: 'park etmeniz', d: 2,
      },
      {
        kind: 'transform', source: 'Полиция разрешила нам уйти.',
        instruction: 'Скажите это по-турецки',
        answer: 'Polis gitmemize izin verdi.', variants: [],
        breakdown: [p('git'), p('me', 'действие'), p('miz', 'наше', 'кто - внутри слова, bize не нужно'), p('e', 'дательный', 'izin vermek - "чему?"')],
        blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Burada sigara içmek yasak.', sourceRu: 'здесь курить нельзя (всем)',
        instruction: 'Скажите лично: "вам здесь курить нельзя"',
        answer: 'Burada sigara içmeniz yasak.', variants: [],
        breakdown: [p('iç'), p('me', 'действие'), p('niz', 'ваше', 'лично кому-то - -mesi с лицом, без падежа')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Мама не разрешила мне пойти.',
        instruction: 'Скажите это по-турецки',
        answer: 'Annem gitmeme izin vermedi.', variants: ['Annem benim gitmeme izin vermedi.'],
        breakdown: [p('git'), p('me', 'действие'), p('m', 'моё', 'bana не нужно'), p('e', 'дательный', 'отрицание - в izin vermedi, а не в действии')],
        blankWhole: true, d: 3,
      },
    ],
  },

  // ── Урок 71: -dığı takdirde ─────────────────────────────────────────────────
  {
    rule: 'conditional-digi-takdirde',
    explain:
      'В договорах и официальных письмах "если" - -dığı takdirde: ödemediğiniz takdirde - "в случае, если вы не заплатите". Это -dığı с лицом (урок 31) плюс takdirde; смысл тот же, что у -sa, и никакого прошедшего здесь нет. В разговоре - -sa, а aksi takdirde - книжное yoksa.',
    examples: [
      {
        parts: [p('Kirayı öde'), p('me', 'не'), p('diğ', '-dığı', 'k перед гласной - ğ'), p('iniz', 'вы'), p(' takdirde', 'в случае, если'), p(' sözleşme biter.')],
        ru: 'в случае неуплаты аренды договор прекращается',
      },
      {
        parts: [p('Geç kal'), p('dığ', '-dığı', 'после a - -dığ'), p('ınız', 'вы'), p(' takdirde', 'в случае, если'), p(' para cezası ödersiniz.')],
        ru: 'в случае опоздания вы заплатите штраф',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'öde', baseRu: 'платить', task: 'в случае, если вы не...',
        context: { tr: 'Kirayı ___ takdirde sözleşme biter.', ru: 'В случае неуплаты аренды договор прекращается.' },
        slots: [b('öde'), m('me', ['me', 'ma', 'mi'], 'не', 'после e - -me'), m('diğ', ['diğ', 'dik', 'dığ'], '-dığı', 'k перед гласной - ğ; после e - -diğ'), m('iniz', ['iniz', 'in', 'i'], 'вы', 'платите вы')],
        answer: 'ödemediğiniz', d: 1,
      },
      {
        kind: 'affix', base: 'kal', baseRu: 'оставаться', task: 'в случае, если вы',
        context: { tr: 'Geç ___ takdirde para cezası ödersiniz.', ru: 'В случае опоздания вы заплатите штраф.' },
        slots: [b('kal'), m('dığ', ['dığ', 'diğ', 'tığ'], '-dığı', 'после a - -dığ; l звонкая - d'), m('ınız', ['ınız', 'iniz', 'ın'], 'вы', 'после ı - -ınız')],
        answer: 'kaldığınız', d: 1,
      },
      {
        kind: 'affix', base: 'gel', baseRu: 'приходить', task: 'в случае, если он не...',
        context: { tr: 'Tanık ___ takdirde duruşma ertelenir.', ru: 'Если свидетель не придёт, заседание отложат.' },
        slots: [b('gel'), m('me', ['me', 'ma', 'mi'], 'не', 'после e - -me'), m('diğ', ['diğ', 'dığ', 'tiğ'], '-dığı', 'после e - -diğ'), m('i', ['i', 'in', 'im'], 'он', 'придёт свидетель - "его"')],
        answer: 'gelmediği', d: 1,
      },
      {
        kind: 'affix', base: 'haber ver', baseRu: 'предупредить', task: 'в случае, если он',
        context: { tr: 'Kiracı iki ay önce ___ takdirde ceza ödemez.', ru: 'При уведомлении за два месяца арендатор штраф не платит.' },
        slots: [b('haber ver'), m('diğ', ['diğ', 'dığ', 'tiğ'], '-dığı', 'после e - -diğ; r звонкая - d'), m('i', ['i', 'in', 'iniz'], 'он', 'предупреждает арендатор - "его"')],
        answer: 'haber verdiği', d: 1,
      },
      {
        kind: 'affix', base: 'kal', baseRu: 'оставаться', task: 'если ты · в разговоре',
        context: { tr: 'Geç ___ beklemem!', ru: 'Опоздаешь - ждать не буду! (другу)' },
        slots: [b('kal'), m('ırsan', ['ırsan', 'dığın takdirde', 'ırsın'], 'если (-sa)', 'в разговоре - -sa; takdirde другу звучит как шутка "по-канцелярски"')],
        answer: 'kalırsan', d: 2,
      },
      {
        kind: 'affix', base: 'öde', baseRu: 'платить', task: 'в случае, если вы не...',
        context: { tr: 'Borcunuzu ___ takdirde kartınız bloke edilecek.', ru: 'В случае неуплаты долга ваша карта будет заблокирована.' },
        slots: [b('öde'), m('mediğiniz', ['mediğiniz', 'mezseniz', 'mediniz'], 'не · -dığı · вы', '"ödemezseniz takdirde" не бывает: либо -sa, либо -dığı takdirde; "ödemediniz" - прошедшее, а речь о будущем')],
        answer: 'ödemediğiniz', d: 2,
      },
      {
        kind: 'affix', base: 'öde', baseRu: 'платить', task: 'в случае, если они не...',
        context: { tr: 'Kiracılar kirayı zamanında ___ takdirde ev sahibi dava açabilir.', ru: 'Если арендаторы не заплатят вовремя, хозяин может подать в суд.' },
        slots: [b('öde'), m('me', ['me', 'ma', 'mi'], 'не', 'после e - -me'), m('dik', ['dik', 'diğ', 'dık'], '-dığı', 'перед -leri k не смягчается'), m('leri', ['leri', 'i', 'lerin'], 'они', 'у "они" - -dıkları / -dikleri')],
        answer: 'ödemedikleri', d: 2,
      },
      {
        kind: 'transform', source: 'Kirayı ödemezseniz sözleşme biter.', sourceRu: 'если не заплатите аренду, договор прекращается',
        instruction: 'Скажите, как в договоре (-dığı takdirde)',
        answer: 'Kirayı ödemediğiniz takdirde sözleşme biter.', variants: [],
        breakdown: [p('öde'), p('me', 'не'), p('diğ', '-dığı', 'k перед гласной - ğ'), p('iniz', 'вы'), p(' takdirde', 'в случае, если')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Geç kalırsanız para cezası ödersiniz.', sourceRu: 'если опоздаете, заплатите штраф',
        instruction: 'Скажите, как в правилах (-dığı takdirde)',
        answer: 'Geç kaldığınız takdirde para cezası ödersiniz.', variants: [],
        breakdown: [p('kal'), p('dığ', '-dığı', 'после a - -dığ'), p('ınız', 'вы'), p(' takdirde', 'в случае, если')],
        d: 3,
      },
      {
        kind: 'transform', source: 'Yoksa dava açarız.', sourceRu: 'иначе подадим в суд',
        instruction: 'Скажите по-книжному: "в противном случае"',
        answer: 'Aksi takdirde dava açarız.', variants: [],
        breakdown: [p('Aksi'), p(' takdirde', 'в противном случае', 'aksi takdirde - книжная сестра yoksa')],
        d: 3,
      },
    ],
  },
]
